<template>
  <div class="api-keys">
    <div class="api-keys__back" @click="changeView('Settings')">
      <i class="ion ion-chevron-back-outline"></i>
      <span>{{ $t("settings.back") }}</span>
    </div>
    <div class="api-keys__elements-container" v-loading="updating">
      <b-overlay :show="loading" class="overlay-div" rounded="sm">
        <div class="api-keys__buttons-toolbar">
          <div class="keys-searcher" :class="{ 'searcher-disabled': !apiKeys.length }">
            <i class="ion ion-search-outline"></i>
            <input
              type="text"
              class="keys-searcher__searcher"
              v-model="keysSearcher"
              placeholder="Search a company..."
              @input="debouncerHandler($event.target.value)"
              :disabled="!apiKeys.length"
            />
            <div class="keys-searcher__spinner" v-if="searchingCompanies">
              <div class="keys-searcher__spinner-icon"></div>
            </div>
            <div
              @click="(keysSearcher = ''), filterKeys('')"
              class="keys-searcher__delete-button"
              v-if="!searchingCompanies && keysSearcher !== ''"
            >
              <i class="ion ion-close-outline"></i>
            </div>
          </div>
          <div class="api-keys__header-buttons">
            <i v-b-popover.hover.top="'Filters'">
              <button class="button-container" @click="openFilters()">
                <el-badge :value="numberFilters">
                  <img src="@/assets/scss/icons/filter.svg" alt="filter-icon" />
                </el-badge>
              </button>
            </i>
          </div>
          <div class="filters-float-container" v-if="showFilters">
            <FiltersApiKeyTable
            :savedFilters="savedFilters"
            @closeFilters="closeFilters"
            @selectedFilters="selectedFilters"
            />
          </div>
          <CompanyKeyGeneratorModal
            @generateNewApiKey="generateNewApiKey"
            v-model="showGeneratorKeyModal"
          />
          <a class="documentation-link" href="https://docs.webservices.goglt.com/" target="_blank"
            >API Docs <i class="ion ion-open-outline"></i
          ></a>
        </div>
        <ApiKeysTable
          v-if="apiKeys.length"
          :apiKeys="filteredApiKeys"
          :updating="updating"
          :allDataHasBeenLoaded="allDataHasBeenLoaded"
          @loadMoreItems="loadMoreItems"
          @sort="sortHandler"
          @updateApiKey="updateApiKey"
          @deleteApiKey="deleteApiKey"
        >
        </ApiKeysTable>
        <template #overlay>
          <div class="loading" v-show="loading">
            <b-icon icon="truck" font-scale="3" animation="cylon" class="loading__text"></b-icon>
            <p class="loading__text">{{ $t("loading") }}...</p>
          </div>
        </template>
      </b-overlay>
      <div class="api-keys__not-found" v-if="!loading && !updating && apiKeys.length === 0">
        <i class="ion ion-file-tray-outline api-keys__not-found-icon"></i>
        <p>No Api Keys found</p>
      </div>
    </div>
  </div>
</template>

<script>
import { debounce } from "@/utils/Debounce";
import { generalMessage } from "../../../utils/ErrorMessages";
import ApiKeysTable from "./components/ApiKeysTable.vue";
import CompanyKeyGeneratorModal from "./components/ApiKeyGenerator.vue";
import FiltersApiKeyTable from "./components/FiltersApiKey.vue";

export default {
  name: "Keys",
  components: {
    ApiKeysTable,
    CompanyKeyGeneratorModal,
    FiltersApiKeyTable,
  },
  data() {
    return {
      loading: true,
      updating: false,
      searchingCompanies: false,
      showGeneratorKeyModal: false,
      skip: 0,
      limit: 100,
      order_by: null,
      allDataHasBeenLoaded: false,
      keysSearcher: "",
      debouncerHandler: null,
      filteredApiKeys: [],
      showFilters: false,
      numberFilters: 0,
      savedFilters: {},
    };
  },
  async created() {
    this.debouncerHandler = debounce((value) => this.filterKeys(value), 300);

    this.getFilterFromLocalStorage();
    this.allDataHasBeenLoaded = false;

    const apiKeys = await this.getApiKeys(this.savedFilters);

    this.apiKeys = [...apiKeys];
    this.filteredApiKeys = [...this.apiKeys];
  },
  beforeDestroy() {
    this.apiKeys = [];
  },
  computed: {
    apiKeys: {
      get() {
        return this.$store.getters["apiKey/getAllApiKeys"];
      },
      set(value) {
        this.$store.commit("apiKey/setAllApiKeys", value);
      },
    },
  },
  methods: {
    async selectedFilters({ filters }) {
      this.updating = true;
      this.savedFilters = filters;
      this.resetVariables();

      this.apiKeys = await this.getApiKeys(this.savedFilters);
      this.filteredApiKeys = [...this.apiKeys];
      this.updating = false;
    },
    async getApiKeys(filters) {
      if (this.allDataHasBeenLoaded) {
        return [];
      }
      this.loading = true;
      const params = {
        ...(filters.company_name && { company_name: filters.company_name.trim() }),
        ...(filters.account_email && { account_email: filters.account_email.trim() }),
        ...(filters.api_key && { api_key: filters.api_key.trim() }),
        ...(filters.webhook_link && { webhook_link: filters.webhook_link.trim() }),
        ...(filters.webhook_is_active !== null && { webhook_is_active: filters.webhook_is_active }),
        ...(filters.is_active !== null && { is_active: filters.is_active }),
      };
      const response = await this.$store.dispatch("apiKey/getAllApiKeys", {
        skip: this.skip,
        limit: this.limit,
        order_by: this.order_by,
        ...(params || {}),
      });
      const apiKeys = response.map((item) => ({
        ...item,
        eyeIcon: "ion ion-eye-off-outline",
      }));
      if (!response.length || response.length < this.limit) {
        this.allDataHasBeenLoaded = true;
      }
      this.loading = false;
      return apiKeys;
    },
    async updateApiKey(payload) {
      this.updating = true;
      await this.$store.dispatch("apiKey/updateStatusApiKey", {
        id: payload.id,
        body: { [payload.key]: payload.value },
      });
      this.$store.commit("apiKey/modifyApiKey", payload);
      const keyIndex = this.apiKeys.findIndex((item) => item.id === payload.id);
      this.apiKeys[keyIndex] = payload;
      this.filteredApiKeys = [...this.apiKeys];
      this.updating = false;
    },
    async deleteApiKey(payload) {
      const deleteConfirmation = await this.swal({
        title: "Do you want to delete this key permanently?",
        icon: "warning",
        buttons: true,
      });
      if (!deleteConfirmation) {
        return;
      }
      this.updating = true;
      this.resetVariables();
      await this.$store.dispatch("apiKey/deleteApiKey", payload.id);
      this.$message({
        type: "success",
        message: "Key successfully deleted",
        duration: 3000,
      });
      this.apiKeys = await this.getApiKeys(this.savedFilters);
      this.filteredApiKeys = [...this.apiKeys];
      this.updating = false;
    },
    async filterKeys(value) {
      const query = value.trim().toLowerCase();
      this.savedFilters = {};
      if (!query) {
        this.resetVariables();
        this.filteredApiKeys = await this.getApiKeys(this.savedFilters);
        return;
      }

      if (query.length < 3) {
        return;
      }

      this.resetVariables();
      this.filteredApiKeys = await this.getApiKeys({ company_name: query });
    },
    async loadMoreItems() {
      this.updating = true;
      this.skip += this.limit;

      const apiKeys = await this.getApiKeys(this.savedFilters);

      this.apiKeys = [...this.apiKeys, ...apiKeys];
      this.filteredApiKeys = [...this.apiKeys];
      this.updating = false;
    },
    async sortHandler(params) {
      this.resetVariables();
      this.updating = true;
      this.order_by = params.order_by;

      this.apiKeys = await this.getApiKeys(this.savedFilters);
      this.filteredApiKeys = [...this.apiKeys];
      this.updating = false;
    },
    async generateNewApiKey(data) {
      if (data) {
        generalMessage("New key generated");
        data.eyeIcon = "ion ion-eye-off-outline";
        data.account = {
          company_name: data.company_name.toUpperCase(),
        };
        this.apiKeys.unshift(data);
        this.filteredApiKeys = [...this.apiKeys];
      }
    },
    resetVariables() {
      this.skip = 0;
      this.limit = 100;
      this.allDataHasBeenLoaded = false;
      this.filteredApiKeys = [];
      this.savedLocalStorageFilter();
      this.badgeFiltersQuantityHandler();
      this.getFilterFromLocalStorage();
    },
    changeView(pathName) {
      if (this.$route.name !== pathName) {
        this.$router.push({
          name: pathName,
        });
      }
    },
    openFilters() {
      this.showFilters = true;
    },
    closeFilters() {
      this.showFilters = false;
    },
    savedLocalStorageFilter() {
      localStorage.setItem("api-key-table", JSON.stringify(this.savedFilters));
    },
    getFilterFromLocalStorage() {
      const infoLocalStorage = JSON.parse(localStorage.getItem("api-key-table"));
      if (infoLocalStorage) {
        // eslint-disable-next-line prefer-object-spread
        this.savedFilters = Object.assign({}, this.savedFilters, infoLocalStorage);
      }
      this.badgeFiltersQuantityHandler();
    },
    badgeFiltersQuantityHandler() {
      const filterKeys = ['company_name', 'account_email', 'api_key', 'webhook_link', 'webhook_is_active', 'is_active'];
      this.numberFilters = filterKeys.filter(
        (key) => this.savedFilters[key] !== null && this.savedFilters[key] !== undefined && this.savedFilters[key] !== ''
      ).length;
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/assets/scss/_mixin' as *;
@use '@/assets/scss/_themes' as *;
@use '@/assets/scss/_variables' as *;
@use '@/assets/scss/buttons/_buttons' as *;
@use '@/assets/scss/_extend' as *;

.api-keys {
  @include splitted-view-styles;
  margin: 0px;
  padding-top: 50px;
  width: 100%;
  &__header {
    display: flex;
    justify-content: end;
    align-items: center;
    margin: 10px;
    gap: 10px;
    position: relative;
    &-buttons {
      display: flex;
      align-items: center;
      gap: 10px;
    }
  }
}

.filters-float-container {
  position: absolute;
  top: 41px;
  right: 15%;
  z-index: 99;
  border: 2px solid $color-border-container;
  border-radius: 10px;
  padding: 5px;
  background-color: $color-white;
}
.button-container {
  border-radius: 5px;
  border: 2px solid $color-border-container;
  color: $color-primary-company;
  background-color: #efefef;
  padding: 0.1rem 0.4rem;
  font-weight: 600;
  font-size: 17px;
  cursor: pointer;
  &:hover {
    border-color: $color-border-container;
    background-color: rgba(0, 185, 204, 0.0588235294);
  }
}
.documentation-link {
  @include font-button-text;
  color: $color-secondary-button-text;
  outline: 0;
  padding: 5px 1%;
  width: auto;
  font-weight: 400;
  i {
    font-size: 16px;
  }
}

.api-keys {
  padding: 0px 5px;
  padding-top: 2%;
  padding-bottom: 1px;
  &__buttons-toolbar {
    margin: 20px 0px;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: end;
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }
  &__elements-container {
    margin: 0px 10px;
  }
  &__back {
    display: flex;
    align-items: center;
    margin-left: 1%;
    @include font-small-standart-text;
    font-weight: bold;
    width: 160px;
    cursor: pointer;
    i {
      font-size: 16px;
      margin-right: 5px;
    }
  }
  &__not-found {
    color: $color-primary-company;
    margin-top: 200px;
    p {
      margin-bottom: 0;
    }
  }
  &__not-found-icon {
    font-size: 50px;
  }
}

.loading {
  margin-top: 450px;
  svg,
  p {
    color: $color-primary-company;
    font-weight: bold;
  }
}

.wrapper {
  display: flex;
}

.keys-searcher {
  position: relative;
  display: flex;
  width: 30%;
  width: 240px;
  height: 35px;
  padding: 6px;
  border-radius: 12px;
  border: 1px solid var(--color-border-container);
  color: var(--color-border-container);
  background-color: #ffffff;
  @media (max-width: 500px) {
    &:nth-of-type(1) {
      order: 3;
    }
  }
  &__searcher {
    width: 100%;
    margin-left: 5px;
    padding-right: 15px;
    background-color: transparent;
    border: none;
    outline: none;
    text-align: left;
    font-family: $font-family-portal;
    @include font-small-standart-text;
    letter-spacing: 0px;
    color: $color-primary-company;
  }
  &__spinner {
    color: #737373;
    position: absolute;
    top: 25%;
    right: 2%;
  }
  &__spinner-icon {
    height: 16px;
    width: 16px;
    color: #5a5a5a;
    position: relative;
    display: inline-block;
    border: 3px solid;
    border-radius: 50%;
    border-top-color: transparent;
    animation: rotate 1s linear infinite;
  }
  &__delete-button {
    cursor: pointer;
    color: #737373;
    position: absolute;
    top: 20%;
    right: 2%;
  }
}

.searcher-disabled {
  background-color: #efefef;
}

::v-deep .bg-light {
  background-color: $color-white !important;
}

::v-deep {
  .rc-searcher-container {
    font-size: 14.5px;
  }
}
</style>
